import React, { useState, useEffect } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Box,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  Avatar,
  Grid,
  Link,
  LinearProgress,
  Autocomplete,
  IconButton,
  InputAdornment, 
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useSharedLink from "../../SharedLink";
import "../../css/normalize.css";
import signinImage from "../../assets/img/abanotes2.png";


const Register = () => {
  const roles = ["BT/RBT", "BCBA"];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");
  const [streetName, setStreetName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pincode, setPincode] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [securityAnswer1, setSecurityAnswer1] = useState("");
  const [securityAnswer2, setSecurityAnswer2] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [step, setStep] = useState(1);
  const [showVerificationLink, setShowVerificationLink] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [dateOfBirthError, setDateOfBirthError] = useState("");
  const [timer, setTimer] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [progress, setProgress] = useState(100);
  const [passwordError, setPasswordError] = useState("");

  const navigate = useNavigate();
  const sharedLink = useSharedLink();
  const [showPassword, setShowPassword] = useState(false);
  const SECRET_KEY = CryptoJS.enc.Hex.parse("4c9edd026c240f960008cdf5f775126bbe0a4154b632ffed169959274376404b");
  const encryptData = (data) => {
    const stringifiedData = JSON.stringify(data);
    return CryptoJS.AES.encrypt(stringifiedData, SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    }).toString();
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  
  useEffect(() => {
    document.body.style.backgroundImage = `url(${signinImage})`;
    document.body.style.backgroundSize = "cover";

    return () => {
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
    };
  }, []);

  const startTimer = () => {
    setIsButtonDisabled(true);
    setTimer(60);
    setProgress(100);
    setSnackbarOpen(true);
    setSnackbarMessage(
      <>
        Verification link has been sent to your email.
        <br />
        If you have not received it, please check that you have entered the
        correct email address and retry after 60 seconds.
      </>
    );
    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          setIsButtonDisabled(false);
          setSnackbarOpen(false);
          return 0;
        }
        setSnackbarMessage(
          <>
            Verification link has been sent to your email.
            <br />
            If you have not received it, please check that you have entered the
            correct email address and retry after {prev - 1} seconds.
          </>
        );
        setProgress((prevProgress) => prevProgress - 100 / 60);
        return prev - 1;
      });
    }, 1000);
  };
  const validatePassword = (value) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{5,}$/;
    if (!regex.test(value)) {
      setPasswordError(
        "Password must contain at least one lowercase, one uppercase, one number, one special character, and be at least 5 characters long."
      );
    } else {
      setPasswordError(""); // Reset error if validation passes
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    localStorage.clear();
    if (passwordError) {
      setSnackbarMessage("Please fix the password issues before proceeding.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return; // Prevent form submission
    }
    const currentDate = new Date().toISOString();
    const requestBody = {
      name,
      email,
      password,
      dateOfBirth: new Date(dateOfBirth).toISOString(),
      phoneNumber,
      gender,
      streetName,
      city,
      state,
      country,
      pincode,
      role,
      securityQuestion1: "Your first college name?",
      securityAnswer1,
      securityQuestion2: "Your favourite sport?",
      securityAnswer2,
      lastUpdatedBy: "admin",
      lastUpdatedTime: currentDate,
      enteredTime: currentDate,
      enteredBy: "admin",
    };
    const encryptedRequestBody = encryptData(requestBody);

    try {
      const res = await axios.post(`${sharedLink}/api/abanotes/addsubscriber`, {
        encryptedData: encryptedRequestBody 
      });
      if (res && res.data) {
        setSnackbarMessage(
          "Verification link sent! Please check your email if you have not received it."
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setShowVerificationLink(true);
        startTimer();
      } else {
        setSnackbarMessage(
          "Email Already Registered. \n Please use a different email"
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
      setSnackbarMessage(error.response?.data || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    localStorage.clear();
    const currentDate = new Date().toISOString();
    try {
      const res = await axios.post(`${sharedLink}/api/abanotes/addsubscriber`, {
        name,
        email,
        password,
        dateOfBirth: new Date(dateOfBirth).toISOString(),
        phoneNumber,
        gender,
        streetName,
        city,
        state,
        country,
        pincode,
        role,
        securityQuestion1: "Your first college name?",
        securityAnswer1,
        securityQuestion2: "Your favourite sport?",
        securityAnswer2,
        lastUpdatedBy: "admin",
        lastUpdatedTime: currentDate,
        enteredTime: currentDate,
        enteredBy: "admin",
      });
      if (res && res.data) {
        setSnackbarMessage(
          "Verification link sent! Please check your email if you have not received it."
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setShowVerificationLink(true);
        startTimer();
      } else {
        setSnackbarMessage(
          "Email Already Registered. \n Please use a different email"
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
      setSnackbarMessage(error.response?.data || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") setName(value);
    if (name === "email") setEmail(value);
    if (name === "dateOfBirth") {
      setDateOfBirth(value);
      const today = new Date();
      const birthDate = new Date(value);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();
      if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      if (age < 18) {
        setDateOfBirthError("You must be at least 18 years old");
      } else {
        setDateOfBirthError("");
      }
    }
    if (name === "phoneNumber") {
      setPhoneNumber(value);
      if (/^\d{10}$/.test(value)) {
        setPhoneNumberError("");
      } else {
        setPhoneNumberError("Phone number must be 10 digits");
      }
    }
    if (name === "gender") setGender(value);
    if (name === "streetName") setStreetName(value);
    if (name === "city") setCity(value);
    if (name === "state") setState(value);
    if (name === "country") setCountry(value);
    if (name === "pincode") setPincode(value);
    if (name === "role") setRole(value);
    if (name === "password") {
      setPassword(value);
      validatePassword(value); // Validate the password
    }
    if (name === "securityAnswer1") setSecurityAnswer1(value);
    if (name === "securityAnswer2") setSecurityAnswer2(value);
  };

  const nextStep = () => {
    if (step === 1) {
      if (
        name &&
        gender &&
        dateOfBirth &&
        phoneNumber &&
        !phoneNumberError &&
        !dateOfBirthError
      ) {
        setStep(step + 1);
      } else {
        setSnackbarMessage("Please fill out all fields before proceeding.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } else if (step === 2) {
      if (streetName && city && state && country && pincode && role) {
        setStep(step + 1);
      } else {
        setSnackbarMessage("Please fill out all fields before proceeding.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Container
      sx={{
        height: "95%",
        width: "90%",
        backgroundColor: "rgba(255, 255, 255, 0.3)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(10px)",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.18)",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box
        className="register-container"
        component="form"
        onSubmit={handleSubmit}
        sx={{
          m: 0,
          p: 1,
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          marginLeft: "20px",
          marginRight: "auto",
          width: "400px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>
        {step === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              margin="normal"
              label="Enter Your Name"
              name="name"
              value={name}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Enter Your Gender"
              name="gender"
              value={gender}
              onChange={handleChange}
              required
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
              select
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              type="date"
              label="Enter Your Date of Birth"
              name="dateOfBirth"
              value={dateOfBirth}
              onChange={handleChange}
              required
              error={Boolean(dateOfBirthError)}
              helperText={dateOfBirthError}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Enter Your Phone Number"
              name="phoneNumber"
              value={phoneNumber}
              onChange={handleChange}
              required
              error={Boolean(phoneNumberError)}
              helperText={phoneNumberError}
              inputProps={{ maxLength: 10 }} // Limits input to 10 characters
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              type="button"
              sx={{
                mt: 1,
                mb: 1,
                backgroundColor: "#e9a132",
                "&:hover": { backgroundColor: "#d8902e" },
              }}
              onClick={nextStep}
            >
              Next
            </Button>
          </Box>
        )}
        {step === 2 && (
          <>
            <TextField
              fullWidth
              margin="normal"
              label="Street Name"
              name="streetName"
              value={streetName}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="City"
              name="city"
              value={city}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="State"
              name="state"
              value={state}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Country"
              name="country"
              value={country}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Pincode"
              name="pincode"
              value={pincode}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 10, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Enter Your Role"
              name="role"
              value={role}
              onChange={handleChange}
              required
              select
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            >
              <MenuItem value="BT/RBT">BT/RBT</MenuItem>
              <MenuItem value="BCBA">BCBA</MenuItem>
            </TextField>

            <Button onClick={prevStep}>Previous</Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              type="button"
              sx={{
                mt: 1,
                mb: 1,
                backgroundColor: "#e9a132",
                "&:hover": { backgroundColor: "#d8902e" },
              }}
              onClick={nextStep}
            >
              Next
            </Button>
          </>
        )}
        {step === 3 && (
          <>
            <TextField
              fullWidth
              margin="normal"
              type="email"
              label="Enter Your Email"
              name="email"
              value={email}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 50, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              type={showPassword ? "text" : "password"}              label="Enter Your Password"
              name="password"
              value={password}
              onChange={handleChange}
              required
              error={Boolean(passwordError)} // Show error if validation fails
              helperText={passwordError} // Display validation message
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography gutterBottom sx={{ mt: 1 }}>
              Security Questions*
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              label="Your first college name?"
              name="securityAnswer1"
              value={securityAnswer1}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Your Favourite sport?"
              name="securityAnswer2"
              value={securityAnswer2}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20, // Limit to 20 characters
              }}
              sx={{
                "& .MuiInputBase-input": {
                  height: "1.1876em",
                  padding: "10px 14px",
                },
              }}
              InputLabelProps={{
                style: {
                  top: "-6px",
                },
              }}
            />
            <Button onClick={prevStep}>Previous</Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              sx={{
                mt: 1,
                mb: 1,
                backgroundColor: "#e9a132",
                "&:hover": { backgroundColor: "#d8902e" },
              }}
              disabled={isButtonDisabled}
            >
              REGISTER
            </Button>
            {isButtonDisabled && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    backgroundColor: "#d0d0d0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#e9a132",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Link
              href="/login"
              variant="body2"
              style={{
                color: "#e9a132",
                textDecoration: "none",
              }}
              onMouseOver={(e) =>
                (e.target.style.textDecoration = "underline purple")
              }
              onMouseOut={(e) => (e.target.style.textDecoration = "none")}
            >
              Already have an account? Sign in
            </Link>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mr: 10 }}>
        <Typography
          className="login-message"
          variant="h4"
          gutterBottom
          sx={{ textAlign: "center" }}
        >
          Welcome to
        </Typography>

        <Typography
          className="login-message"
          variant="h2"
          gutterBottom
          sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
        >
          ABA Notes
        </Typography>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={60000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Register;
